import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";

import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useNotificationSocket } from "Requests/useNotificationSocket";
import { useChatSocket } from "Requests/useChatSocket";
import { api, serverUrl } from "Requests/Api";
import { useAppContext } from "State/Global/AppContext";
import { useStorage } from "Hooks/useStorage";
import { Button, notification, Checkbox, Spin } from "antd";
import { Logger, ErrorBoundaryWithLogger } from "../../frontend-lib/Log";
import { AgentError } from "./AgentError";
import CTA from "Constants";
import Card from "Components/Card/Card";
import ChatList from "Components/ChatList/ChatList";
import ChatHandler from "Components/ChatHandler/Chahandler/ChatHandler";
import ChatInfo from "Components/ChatInfo/ChatInfo";
import styles from "./Agent.module.scss";
import {
    getSessionIdCookieLocalStorage,
    parseCookiesGetSessionIdCookie,
} from "Requests/socketConfigs";
import { isNativeIos, isNativeMobile } from "frontend-lib/Utils/Mobile";

const {
    agent,
    agentView,
    agentViewMobile,
    spinner,
    chatListWrapper,
    chatHandlerWrapper,
    chatInfoWrapper,
} = styles;
function Agent() {
    const { t } = useTranslation();
    const [activeView, setActiveView] = useState(CTA.VIEWS.CHAT_LIST);
    const [isMobile, setIsMobile] = useState(false);
    const [wrapperHeight, setWrapperHeight] = useState(null);
    const [isPredictModalVisible, setIsPredictModalVisible] = useState(false);

    const [
        notificationSocket,
        {
            connectNotificationSocket,
            disconnectNotificationSocket,
            onNotificationSocketReconnecting,
            onNotificationSocketReconnect,
        },
    ] = useNotificationSocket();

    const [
        chatSocket,
        {
            connectChatSocket,
            disconnectChatSocket,
            onChatSocketReconnecting,
            onChatSocketReconnect,
        },
    ] = useChatSocket();

    const [showInstallModal, setShowInstallModal] = useStorage(
        { show: true },
        CTA.LOCAL_STORAGE_KEYS.INSTALL_PROMPT
    );

    const { actions, appState } = useAppContext();
    const wrapperRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();
    const logger = useMemo(() => new Logger("Agent"), []);

    const isLoading = useMemo(() => {
        return appState.isLoading;
    }, [appState.isLoading]);

    const notificationSocketStatus = useMemo(() => {
        return appState.notificationSocketStatus;
    }, [appState.notificationSocketStatus]);

    const openChat = useCallback(
        (chat) => {
            actions.setSelectedChat(chat);
            navigate(CTA.VIEWS.CHAT_HANLDER);
        },
        [actions, navigate]
    );

    async function closeChat() {
        // setActiveView is also set when calling `navigate`
        // This is also called here to prevent a rerender of ChatHandler with an empty chat
        // Calling `navigate` before calling `setSelectedChat` does not prevent that
        setActiveView(CTA.VIEWS.CHAT_LIST);
        actions.setSelectedChat({});
        actions.setNoDeepLinkChatFound(false);
        navigate(CTA.VIEWS.CHAT_LIST);
    }

    function openInfo() {
        navigate(CTA.VIEWS.CHAT_INFO);
    }

    function closeInfo() {
        navigate(CTA.VIEWS.CHAT_HANLDER);
    }

    function loadTags() {
        api.tags()
            .list()
            .then((res) => {
                actions.setConversationLabels(res.data.conversationLabels);
            })
            .catch((err) => {
                logger.error(err);
            });
    }

    function installApp(e) {
        if (e) {
            e.prompt();
            e.userChoice.then((choiceResult) => {
                if (choiceResult.outcome === "accepted") {
                    console.log("User accepted the install prompt");
                } else {
                    console.log("User dismissed the install prompt");
                }
            });
        }
        notification.close("install");
    }

    function triggerInstallModal(e) {
        if (!showInstallModal.show) {
            return;
        }
        notification.open({
            key: "install",
            description: (
                <>
                    <p>{t("installApp")}</p>
                    <Checkbox onChange={(e) => setShowInstallModal({ show: !e.target.checked })}>
                        {t("notifications.dontAskText")}
                    </Checkbox>
                </>
            ),
            btn: (
                <Button type="primary" size="small" onClick={() => installApp(e)}>
                    {t("install")}
                </Button>
            ),
            onClose: () => console.log("Close"),
            placement: "bottom",
            duration: 8,
        });
    }

    function getInstalationEvent() {
        window.addEventListener("beforeinstallprompt", (e) => {
            e.preventDefault();
            triggerInstallModal(e);
        });
    }

    const chatSocketConnectionError = useCallback(() => {
        actions.setChatSocketStatus(CTA.SOCKET_STATUS.ERROR);
    }, [actions]);

    const notificationSocketConnectionError = useCallback(() => {
        actions.setNotificationSocketStatus(CTA.SOCKET_STATUS.ERROR);
    }, [actions]);

    const logout = async () => {
        const loaderHash = actions.setLoader();
        try {
            disconnectNotificationSocket();
            disconnectChatSocket();
            await api.auth().signOut();
            actions.setSelectedChat({});
            actions.unsetLoader(loaderHash);
            actions.setAuth(false);
        } catch (err) {
            actions.unsetLoader(loaderHash);
            console.log(err);
        }
    };

    useEffect(() => {
        async function signOutIfNoCookie() {
            const cookie =
                parseCookiesGetSessionIdCookie(document.cookie) ||
                (await getSessionIdCookieLocalStorage());
            if (!cookie) logout();
        }
        // This prevents a bug in ios where the socket cannot connect because there is no cookie
        // If there is no cookie, logout and set cookie again
        if (isNativeIos()) signOutIfNoCookie();
    }, []);

    function connectSockets() {
        const loaderHash = actions.setLoader();

        try {
            connectNotificationSocket(appState.workspaceConfig?.socketConfiguration);
        } catch (e) {
            actions.setChatSocketStatus(CTA.SOCKET_STATUS.ERROR);
            logger.error(
                `Error in Agent.js/connectSockets connecting notification socket with configs ${JSON.stringify(
                    appState.workspaceConfig?.socketConfiguration
                )}. Error is: ${JSON.stringify(e)} ${e}`
            );
        }

        try {
            connectChatSocket(appState.workspaceConfig?.socketConfiguration);
        } catch (e) {
            actions.setNotificationSocketStatus(CTA.SOCKET_STATUS.ERROR);
            logger.error(
                `Error in Agent.js/connectSockets connecting chat socket with configs ${JSON.stringify(
                    appState.workspaceConfig?.socketConfiguration
                )}. Error is: ${JSON.stringify(e)}`
            );
        }

        actions.unsetLoader(loaderHash);
    }

    useEffect(() => {
        if (notificationSocket) {
            notificationSocket.on("connect", () => {});
            notificationSocket.on("connect_error", notificationSocketConnectionError);
            notificationSocket.on("error", notificationSocketConnectionError);
        }
    }, [notificationSocket, notificationSocketConnectionError]);

    useEffect(() => {
        if (chatSocket) {
            chatSocket.on("connect", () => {
                actions.setChatSocketStatus(CTA.SOCKET_STATUS.CONNECTED);
            });
            chatSocket.on("connect_error", chatSocketConnectionError);
            chatSocket.on("error", chatSocketConnectionError);
        }
    }, [chatSocket, actions, chatSocketConnectionError]);

    useEffect(() => {
        const view = location.pathname.substring(1);
        if (Object.values(CTA.VIEWS).includes(view)) {
            setActiveView(view);
        } else {
            setActiveView(CTA.VIEWS.CHAT_LIST);
        }
    }, [location]);

    useEffect(() => {
        getInstalationEvent();
        setIsMobile(window.innerWidth < 1024);
        setWrapperHeight(wrapperRef.current?.clientHeight);
        loadTags();
        connectSockets();

        window.addEventListener("resize", () => {
            setWrapperHeight(wrapperRef.current?.clientHeight);
            setIsMobile(window.innerWidth < 1024);
        });

        window.addEventListener("offline", () => {
            onNotificationSocketReconnecting();
            onChatSocketReconnecting();
        });

        window.addEventListener("online", () => {
            onNotificationSocketReconnect();
            onChatSocketReconnect();
        });
        return () => {
            window.removeEventListener("resize", () => {});
            window.removeEventListener("offline", () => {});
            window.removeEventListener("online", () => {});
        };
        // eslint-disable-next-line
    }, []);

    return (
        <ErrorBoundaryWithLogger
            serverUrl={serverUrl}
            logName="agent"
            badRenderBody={() => <AgentError logoutCb={logout} />}
        >
            <div ref={wrapperRef} className={`${agent} ${isMobile && agentViewMobile}`}>
                <Card className={`${agent} ${isMobile && agentViewMobile}`}>
                    {isLoading && <Spin spinning={isLoading} className={spinner}></Spin>}
                    <ChatList
                        className={`${agentView} ${chatListWrapper}`}
                        active={activeView === CTA.VIEWS.CHAT_LIST}
                        isMobile={isMobile}
                        notificationSocket={notificationSocket}
                        openChatCb={openChat}
                        logoutCb={logout}
                    />
                    <ChatHandler
                        className={`${agentView} ${chatHandlerWrapper}`}
                        active={activeView === CTA.VIEWS.CHAT_HANLDER}
                        isMobile={isMobile}
                        chatSocket={chatSocket}
                        openInfoCb={openInfo}
                        closeChatCb={closeChat}
                        height={wrapperHeight}
                        handleOpenPredictModal={() => setIsPredictModalVisible(true)}
                    />
                    <ChatInfo
                        className={`${agentView} ${chatInfoWrapper}`}
                        active={activeView === CTA.VIEWS.CHAT_INFO}
                        isMobile={isMobile}
                        closeInfoCb={closeInfo}
                        isPredictModalVisible={isPredictModalVisible}
                        handleClosePredictModal={() => setIsPredictModalVisible(false)}
                    />
                </Card>
            </div>
        </ErrorBoundaryWithLogger>
    );
}
export default Agent;
